<template>
    <div class="ps">
        <ProductImageViewerCmp
            :images="viewer.images"
            :start-index="viewer.index"
            :visible="viewer.visible"
            @close="viewer.visible = false" />

        <header class="ps-hero" data-tour="product-hero">
            <button type="button" class="ps-hero-thumb"
                :class="{'is-empty': !heroImages.length, 'is-deck': heroImages.length > 1}"
                :disabled="!heroImages.length"
                :aria-label="heroImages.length > 1 ? `Ver as ${heroImages.length} imagens do produto` : 'Ver imagem do produto'"
                @click="openImageViewer(heroImages, 0)">
                <span v-for="(img, i) in heroDeck" :key="`${i}-${img.url}`"
                    class="ps-deck-card" :class="[`is-depth-${i}`, {'is-inactive': !img.isActive}]">
                    <img :src="img.url" :alt="i === 0 ? 'Imagem principal do produto' : ''" />
                </span>
                <fa v-if="!heroImages.length" icon="images" />
                <span v-if="heroImages.length > 1" class="ps-deck-count">+{{heroImages.length - 1}}</span>
            </button>

            <div class="ps-hero-main">
                <p class="ps-eyebrow">Produto simples</p>
                <h2 class="ps-hero-title">{{product.name}}</h2>

                <div class="ps-hero-chips">
                    <span class="ps-status" :class="product.isActive ? 'is-active' : 'is-inactive'">
                        <span class="ps-status-dot"></span>
                        {{product.isActive ? 'Ativo' : 'Inativo'}}
                    </span>
                    <span class="ps-chip">
                        <small>SKU</small><code>{{product.skuCode | infoOrPlaceholder}}</code>
                        <CopyButtonCmp v-if="product.skuCode" class="ps-chip-copy" :value="product.skuCode" label="SKU" />
                    </span>
                    <span class="ps-chip">
                        <small>Cód. retaguarda</small><code>{{product.codeSource | infoOrPlaceholder}}</code>
                        <CopyButtonCmp v-if="product.codeSource" class="ps-chip-copy" :value="product.codeSource" label="código da retaguarda" />
                    </span>
                    <a :href="urlToOrder" target="_blank" class="ps-hero-link">
                        Pedidos com este produto
                        <fa icon="external-link-alt" />
                    </a>
                </div>
            </div>

            <dl class="ps-hero-figures" v-if="!product.hasMoreThanOneStore()">
                <div>
                    <dt>Venda</dt>
                    <dd>{{product.sellPrice | currency}}</dd>
                </div>
                <div>
                    <dt>Disponível</dt>
                    <dd>{{product.totalAvailableStockQtyInStores() | infoOrPlaceholder}}</dd>
                </div>
            </dl>
        </header>

        <div class="ps-grid">
            <div class="ps-col">
                <section class="ps-section ps-desc" :class="{'is-collapsed': !descriptionOpen}">
                    <div class="ps-section-head ps-desc-head">
                        <button type="button" class="ps-desc-toggle"
                            :aria-expanded="String(descriptionOpen)" aria-controls="ps-description-panel"
                            :title="descriptionOpen ? 'Recolher descrição' : 'Mostrar descrição'"
                            @click="toggleDescription">
                            <fa icon="chevron-down" class="ps-desc-chevron" />
                            <span class="ps-section-title">
                                Descrição<template v-if="descriptionOpen"> {{currentDescription.label.toLowerCase()}}</template>
                            </span>

                            <template v-if="!descriptionOpen">
                                <span class="ps-desc-pills">
                                    <span v-for="tab in descriptionTabs" :key="tab.id"
                                        class="ps-desc-pill" :class="{'has-text': tab.has}"
                                        :title="`Descrição ${tab.label.toLowerCase()} ${tab.has ? 'cadastrada' : 'não cadastrada'}`">
                                        {{tab.label}}
                                    </span>
                                </span>
                                <span class="ps-desc-preview" :class="{'is-empty': !collapsedPreview}">
                                    {{collapsedPreview || 'Nenhuma descrição cadastrada'}}
                                </span>
                            </template>
                        </button>

                        <div v-if="descriptionOpen" class="ps-tabs" role="tablist" aria-label="Tipo de descrição">
                            <button v-for="tab in descriptionTabs" :key="tab.id" type="button" role="tab"
                                class="ps-tab" :class="{'is-active': descriptionTab === tab.id, 'is-empty': !tab.has}"
                                :aria-selected="String(descriptionTab === tab.id)"
                                @click="descriptionTab = tab.id">
                                {{tab.label}}
                            </button>
                        </div>
                    </div>

                    <div id="ps-description-panel" class="ps-collapse" :class="{'is-open': descriptionOpen}"
                        :aria-hidden="String(!descriptionOpen)">
                        <div class="ps-collapse-inner">
                            <div class="ps-description" role="tabpanel">
                                <p v-if="currentDescription.has" v-html="currentDescription.html"></p>
                                <p v-else class="ps-empty">Descrição {{currentDescription.label.toLowerCase()}} não cadastrada.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="ps-channels" data-tour="product-channels" v-if="singleStoreChannels.length">
                    <div class="ps-channels-head">
                        <h3 class="ps-section-title">Canais</h3>
                        <span class="ps-count">{{singleStoreChannels.length}}</span>
                    </div>
                    <p class="ps-section-description">
                        Cada canal confere se reconhece o ID associado a este produto.
                    </p>

                    <ProductChannelCardListCmp
                        class="ps-channel-list"
                        :store-channels="singleStoreChannels"
                        :channel-list="channelList"
                        :id-store="singleStoreId"
                        :id-muven="idMuven"
                        :product-summary="productSummary"
                        @updated="(storeChannel, newId) => onChannelIdUpdated(singleStoreId, storeChannel, newId)"
                        @listing-changed="(storeChannel, result) => onListingChanged(singleStoreId, storeChannel, result)" />
                </section>

                <section class="ps-section">
                    <div class="ps-section-head">
                        <h3 class="ps-section-title">Imagens</h3>
                        <span class="ps-count" v-if="product.hasImages()">{{product.getImages().length}}</span>
                    </div>

                    <p v-if="!product.hasImages()" class="ps-empty">Nenhuma imagem vinculada.</p>

                    <div v-else class="ps-gallery">
                        <figure v-for="(img, index) in product.getImages()" :key="index" class="ps-image"
                            :class="{'is-main': img.isMain && img.isActive, 'is-inactive': !img.isActive}">
                            <button type="button" @click="openImageViewer(product.getImages(), index)">
                                <img :src="img.url" alt="Imagem do produto">
                            </button>
                            <figcaption>
                                <span>{{img.getSizeInformation()}}</span>
                                <span v-if="img.isMain && img.isActive" class="ps-image-badge">Principal</span>
                                <span v-if="!img.isActive" class="ps-image-badge is-muted">Inativa</span>
                            </figcaption>
                        </figure>
                    </div>
                </section>

                <section class="ps-section store-info-card" data-tour="product-channels" v-if="product.hasMoreThanOneStore() && product.hasStores()">
                    <h3 class="ps-section-title">Informações por loja</h3>

                    <dl class="ps-facts">
                        <div>
                            <dt>Estoque somado</dt>
                            <dd>{{product.totalStockQtyInStores()}}</dd>
                        </div>
                    </dl>

                    <div class="ps-store-picker">
                        <label class="ps-label">Loja</label>
                        <multiselect
                            v-model="chosenStore"
                            :options="productStoreList"
                            :multiple="false"
                            :show-labels="false"
                            :allow-empty="false"
                            label="name"
                            track-by="id"
                            @select="chooseStoreFromList"
                            placeholder="" />
                        <small v-show="!!chosenProductStore.updatedAt" class="text-muted">Última atualização em {{chosenProductStore.updatedAt | dateTimeFilter | infoOrPlaceholder}}</small>
                    </div>

                    <dl class="ps-facts">
                        <div>
                            <dt>Preço de custo</dt>
                            <dd>{{chosenProductStore.costPrice | currency}}</dd>
                        </div>
                        <div>
                            <dt>Preço de listagem</dt>
                            <dd>{{chosenProductStore.listPrice | currency}}</dd>
                        </div>
                        <div>
                            <dt>Preço de venda</dt>
                            <dd>{{chosenProductStore.sellPrice | currency}}</dd>
                        </div>
                        <div>
                            <dt>Estoque</dt>
                            <dd>{{chosenProductStore.stockQty | infoOrPlaceholder}}</dd>
                        </div>
                        <div>
                            <dt>Estoque reservado</dt>
                            <dd>
                                {{chosenProductStore.reservedStockQty | infoOrPlaceholder}}
                                <a :href="urlToReservedStockOrder" 
                                    target="_blank"    
                                    v-show="!!chosenProductStore.reservedStockQty"
                                    class="text-muted ml-2">
                                    <fa icon="external-link-alt" />
                                </a>
                            </dd>
                        </div>
                        <div>
                            <dt>Estoque disponível</dt>
                            <dd>{{chosenProductStore.totalAvailableStockQty ? chosenProductStore.totalAvailableStockQty() : '-'}}</dd>
                        </div>
                    </dl>

                    <ProductChannelCardListCmp
                        v-if="chosenProductStoreHasChannels"
                        class="ps-channel-list"
                        :key="`store-${chosenProductStore.idStore}`"
                        :store-channels="chosenProductStore.channels"
                        :channel-list="channelList"
                        :id-store="chosenProductStore.idStore"
                        :id-muven="idMuven"
                        :product-summary="productSummary"
                        @updated="(storeChannel, newId) => onChannelIdUpdated(chosenProductStore.idStore, storeChannel, newId)"
                        @listing-changed="(storeChannel, result) => onListingChanged(chosenProductStore.idStore, storeChannel, result)" />
                </section>
            </div>

            <aside class="ps-col">
                <section class="ps-section" data-tour="product-backend">
                    <h3 class="ps-section-title">Retaguarda</h3>

                    <div class="pr-ids">
                        <div class="pr-id">
                            <span class="pr-id-label">Id</span>
                            <code>{{product.idSource | infoOrPlaceholder}}</code>
                            <CopyButtonCmp v-if="product.idSource" class="pr-id-copy" :value="product.idSource" label="id na retaguarda" />
                        </div>
                        <div class="pr-id">
                            <span class="pr-id-label">Código</span>
                            <code>{{product.codeSource | infoOrPlaceholder}}</code>
                            <CopyButtonCmp v-if="product.codeSource" class="pr-id-copy" :value="product.codeSource" label="código na retaguarda" />
                        </div>
                    </div>

                    <div class="pr-group">
                        <p class="pr-group-title">
                            O que vem da retaguarda
                            <InfoPopoverCmp title="O que vem da retaguarda?">
                                <p>
                                    Mostra o que a <em>retaguarda (ERP)</em> pode atualizar neste produto pela integração.
                                    O que aparece em <strong>roxo</strong> chega do ERP; o que está apagado é mantido no
                                    <strong>Muven</strong> e não é sobrescrito.
                                </p>
                                <p v-if="product.getAllowImportDescription()"><strong>Neste produto:</strong> {{product.getAllowImportDescription().toLowerCase()}}.</p>
                                <p>Para mudar, edite o produto e ajuste a <strong>importação da retaguarda</strong>.</p>
                            </InfoPopoverCmp>
                        </p>
                        <ul class="pr-scope">
                            <li v-for="item in importScope" :key="item.key" :class="{'is-included': item.included}">
                                <fa :icon="item.included ? 'check' : 'minus'" />
                                {{item.label}}
                                <span class="sr-only">{{item.included ? 'importado' : 'não importado'}}</span>
                            </li>
                        </ul>
                    </div>

                    <div class="pr-group">
                        <p class="pr-group-title">Preço e estoque <span>· vindos do ERP</span></p>
                        <template v-if="!product.hasMoreThanOneStore()">
                            <div class="ps-metrics pr-metrics">
                                <div>
                                    <span>Custo</span>
                                    <strong>{{product.costPrice | currency}}</strong>
                                </div>
                                <div>
                                    <span>Listagem</span>
                                    <strong>{{product.listPrice | currency}}</strong>
                                </div>
                                <div>
                                    <span>Venda</span>
                                    <strong>{{product.sellPrice | currency}}</strong>
                                </div>
                                <div>
                                    <span>Estoque</span>
                                    <strong>{{product.totalStockQtyInStores() | infoOrPlaceholder}}</strong>
                                </div>
                                <div>
                                    <span>
                                        Separado
                                        <a :href="urlToReservedStockOrder" 
                                            target="_blank"
                                            v-show="!!product.totalReservedStockQtyInStores()"
                                            title="Pedidos pendentes com este produto">
                                            <fa icon="external-link-alt" />
                                        </a>
                                    </span>
                                    <strong>{{product.totalReservedStockQtyInStores() | infoOrPlaceholder}}</strong>
                                </div>
                                <div>
                                    <span>Disponível</span>
                                    <strong>{{product.totalAvailableStockQtyInStores() | infoOrPlaceholder}}</strong>
                                </div>
                            </div>
                        </template>
                        <div class="pr-conversion" :class="{'has-conversion': stockConversion.hasConversion}">
                            <fa icon="exchange-alt" class="pr-conversion-icon" />
                            <div>
                                <strong>{{stockConversion.title}}</strong>
                                <span>{{stockConversion.short}}</span>
                            </div>
                            <InfoPopoverCmp class="pr-conversion-info" title="O que é a conversão de estoque?">
                                <p>
                                    É o fator que a <em>retaguarda (ERP)</em> informa para dizer quantas unidades de venda
                                    equivalem a 1 unidade do estoque de lá. O <strong>Muven</strong> só exibe esse valor. Todo o cálculo é feito pelo canal.
                                </p>
                                <p>
                                    Exemplo: o ERP controla em <strong>caixa com 12</strong> e o canal vende por unidade,
                                    então o fator é 12. Com fator <strong>1</strong>, estoque e venda usam a mesma unidade.
                                </p>
                                <p>
                                    Se o estoque no canal não bater com o do ERP, confira esse fator: a diferença de unidade
                                    costuma ser o motivo.
                                </p>
                            </InfoPopoverCmp>
                        </div>
                    </div>

                    <dl class="pr-meta">
                        <div>
                            <dt>Prazo adicional de entrega</dt>
                            <dd>{{product.additionalDeliveryDays | deliveryDays}}</dd>
                        </div>
                        <div>
                            <dt>Cadastro atualizado em</dt>
                            <dd>{{product.updatedAt | dateTimeFilter | infoOrPlaceholder}}</dd>
                        </div>
                        <div v-if="product.hasStores() && !product.hasMoreThanOneStore()">
                            <dt>Loja atualizada em</dt>
                            <dd>{{product.stores[0].updatedAt | dateTimeFilter | infoOrPlaceholder}}</dd>
                        </div>
                    </dl>
                </section>

                <section class="ps-section" v-if="!product.isVirtual">
                    <h3 class="ps-section-title">Cubagem</h3>

                    <div class="ps-metrics is-two">
                        <div>
                            <span>Peso</span>
                            <strong>{{product.weight | infoOrPlaceholder}} <small>kg</small></strong>
                        </div>
                        <div>
                            <span>Altura</span>
                            <strong>{{product.height | infoOrPlaceholder}} <small>cm</small></strong>
                        </div>
                        <div>
                            <span>Largura</span>
                            <strong>{{product.width | infoOrPlaceholder}} <small>cm</small></strong>
                        </div>
                        <div>
                            <span>Profundidade</span>
                            <strong>{{product.depth | infoOrPlaceholder}} <small>cm</small></strong>
                        </div>
                    </div>
                </section>
                <section class="ps-section">
                    <h3 class="ps-section-title">Catálogo</h3>

                    <dl class="ps-facts">
                        <template v-if="!product.hasMoreThanOneStore()">
                            <div>
                                <dt>NCM</dt>
                                <dd class="ps-copy-row"><span>{{product.ncmCode | infoOrPlaceholder}}</span><CopyButtonCmp v-if="product.ncmCode" class="ps-dd-copy" :value="product.ncmCode" label="NCM" /></dd>
                            </div>
                            <div>
                                <dt>EAN</dt>
                                <dd class="ps-copy-row"><span>{{product.eanCode | infoOrPlaceholder}}</span><CopyButtonCmp v-if="product.eanCode" class="ps-dd-copy" :value="product.eanCode" label="EAN" /></dd>
                            </div>
                            <div>
                                <dt>Tipo de produto</dt>
                                <dd>{{product.isVirtual ? 'Virtual' : 'Físico'}}</dd>
                            </div>
                        </template>
                        <div>
                            <dt>Marca</dt>
                            <dd>{{product.getBrand().name | infoOrPlaceholder}}</dd>
                        </div>
                        <div class="is-wide">
                            <dt>Categorias</dt>
                            <dd>
                                <span v-if="!product.hasCategories()">-</span>
                                <span v-for="(productCategoryChannel, index) in product.getCategories()" :key="index" class="ps-tag">
                                    {{productCategoryChannel.category.name | infoOrPlaceholder}}
                                </span>
                            </dd>
                        </div>
                    </dl>
                </section>

                <section class="ps-section">
                    <h3 class="ps-section-title">SEO e vídeo</h3>

                    <dl class="ps-facts">
                        <div class="is-wide">
                            <dt>Título</dt>
                            <dd>{{product.seoTitle | infoOrPlaceholder}}</dd>
                        </div>
                        <div class="is-wide">
                            <dt>Descrição</dt>
                            <dd><ProductScrollTextCmp>{{product.seoDescription | infoOrPlaceholder}}</ProductScrollTextCmp></dd>
                        </div>
                        <div class="is-wide">
                            <dt>Palavras chave</dt>
                            <dd><ProductScrollTextCmp>{{product.seoKeywords | infoOrPlaceholder}}</ProductScrollTextCmp></dd>
                        </div>
                        <div class="is-wide">
                            <dt>URL do vídeo</dt>
                            <dd class="ps-break">{{product.videoUrl | infoOrPlaceholder}}</dd>
                        </div>
                    </dl>
                </section>
            </aside>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { ProductModel } from '@/product/models/ProductModel'
import { dateTimeFilter } from '@/utils/filters/DateTimeFilter'
import { infoOrPlaceholder } from '@/utils/filters/InformationOrPlaceholderFilter'
import { EntityEnum, OrderStatusEnum } from '@c2b/muven-commons'
import GetAuditUrl from '../../utils/mixins/GetAuditUrl'
import { StoreModel } from '@/store/models/StoreModel'
import { ProductStoreModel } from '../models/ProductStoreModel'
import { ProductStoreChannelModel } from '../models/ProductStoreChannelModel'
import ProductChannelCardListCmp from './ProductChannelCardListCmp.vue'
import ProductImageViewerCmp from './ProductImageViewerCmp.vue'
import CopyButtonCmp from '@/utils/components/CopyButtonCmp.vue'
import InfoPopoverCmp from '@/utils/components/InfoPopoverCmp.vue'
import ProductScrollTextCmp from './ProductScrollTextCmp.vue'
import { orderImagesForDisplay, hasActiveImage } from '../utils/ProductImageOrder'
import { descriptionPreview, readDescriptionOpen, saveDescriptionOpen } from '../utils/DescriptionPanelHelper'
import { describeStockConversion } from '../utils/StockConversionHelper'
import { getImportScope } from '../utils/AllowImportHelper'
import { deliveryDays } from '../utils/DeliveryDaysFilter'

export default {
    name: 'ProductSimpleCmp',    
    components: {
        ProductChannelCardListCmp,
        ProductImageViewerCmp,
        CopyButtonCmp,
        InfoPopoverCmp,
        ProductScrollTextCmp,
    },
    props: {
        product: {
            required: true,
            type: ProductModel,
            default: new ProductModel(),
        },
        // Canais configurados do assinante: é deles que vem se o canal está ativo
        channelList: {
            type: Array,
            default: () => [],
        },
    },
    data() {
       return {
            EntityEnum,
            viewer: {images: [], index: 0, visible: false},
            productStoreList: [],
            chosenStore: new StoreModel(),
            chosenProductStore: new ProductStoreModel(),
            descriptionTab: 'main',
            descriptionOpen: readDescriptionOpen(),
            breadcrumbList: [
                {
                    id: 0,
                    description: 'Produtos',
                    path: '/produtos',
                },
                {
                    id: 1,
                    description: `Detalhes de #${this.$route.params.id}`,
                    path: '',
                },
            ],
       } 
    },
    filters: {
        dateTimeFilter,        
        infoOrPlaceholder,
        deliveryDays,
    },
    mixins: [
        GetAuditUrl,
    ],
    methods: {
        toggleDescription() {
            this.descriptionOpen = !this.descriptionOpen
            saveDescriptionOpen(this.descriptionOpen)
        },
        openImageViewer(images, index = 0) {
            const list = images || []

            if (!list.length) {
                return
            }

            this.viewer = {images: list, index, visible: true}
        },
        gotoModification(product) {
            this.$router.push(`/produtos/${product.id}/edicao`)
        },
        chooseStoreFromList(store) {
            this.chosenStore = store
            this.chosenProductStore = this.product.getProductStoreInfo(store)
        },
        // O card já gravou a nova associação; aqui só refletimos no produto
        // carregado, sem buscar tudo de novo.
        onChannelIdUpdated(idStore, storeChannel, newId) {
            const oldId = storeChannel.idExternalChannel

            this.product.stores
                .filter((productStore) => String(productStore.idStore) === String(idStore))
                .forEach((productStore) => {
                    productStore.channels
                        .filter((ch) => String(ch.channel.id) === String(storeChannel.channel.id) && ch.idExternalChannel === oldId)
                        .forEach((ch) => {
                            ch.idExternalChannel = newId
                        })
                })
        },
        // Mercado Livre multi: cada card é uma linha da product_store_channel (ver ProductChannelAssociationModalCmp)
        onListingChanged(idStore, storeChannel, {action, idExternalChannel, id}) {
            this.product.stores
                .filter((productStore) => String(productStore.idStore) === String(idStore))
                .forEach((productStore) => {
                    const sameChannel = (ch) => String(ch.channel.id) === String(storeChannel.channel.id)
                    const row = productStore.channels.find((ch) => sameChannel(ch) && (storeChannel.id
                        ? String(ch.id) === String(storeChannel.id)
                        : (ch.idExternalChannel || null) === (storeChannel.idExternalChannel || null)))

                    if (action === 'add') {
                        const siblings = productStore.channels.filter(sameChannel)
                        const after = productStore.channels.indexOf(siblings[siblings.length - 1])
                        const added = new ProductStoreChannelModel({...(row || storeChannel), id: id || undefined, idExternalChannel})

                        productStore.channels.splice(after + 1, 0, added)
                        return
                    }

                    if (!row) {
                        return
                    }

                    if (action === 'remove') {
                        productStore.channels.splice(productStore.channels.indexOf(row), 1)
                        return
                    }

                    row.idExternalChannel = idExternalChannel
                    row.id = id || undefined
                })
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        idMuven() {
            // Produto simples da Bimer: a associação fica no filho
            return this.product.firstChildId || this.product.id
        },
        singleStoreId() {
            return this.product.hasStores() ? this.product.stores[0].idStore : null
        },
        // Com mais de uma loja os canais aparecem por loja, em "Informações por loja"
        singleStoreChannels() {
            if (!this.product.hasStores() || this.product.hasMoreThanOneStore()) {
                return []
            }

            return this.product.getUniqueStoreChannels()
        },
        // O que a associação guiada mostra do lado do Muven
        productSummary() {
            return {
                name: this.product.name,
                skuCode: this.product.skuCode,
                codeSource: this.product.codeSource,
                eanCode: this.product.eanCode,
                imageUrl: this.mainImageUrl,
            }
        },
        mainImageUrl() {
            const images = this.product.getImages() || []
            const main = images.find((img) => img.isMain && img.isActive) || images.find((img) => img.isActive)

            return main ? main.url : ''
        },
        importScope() {
            return getImportScope(this.product.allowImport)
        },
        stockConversion() {
            return describeStockConversion(this.product.stockConversionRatio)
        },
        // Topo em "baralho": a principal na frente e as demais atrás
        heroImages() {
            const images = this.product.getImages()

            return hasActiveImage(images) ? orderImagesForDisplay(images) : []
        },
        heroDeck() {
            return this.heroImages.slice(0, 3)
        },
        descriptionTabs() {
            return [
                {id: 'main', label: 'Principal', has: this.product.hasDescription()},
                {id: 'short', label: 'Curta', has: this.product.hasDescriptionShort()},
                {id: 'long', label: 'Longa', has: this.product.hasDescriptionLong()},
            ]
        },
        currentDescription() {
            const tab = this.descriptionTabs.find((t) => t.id === this.descriptionTab)
            const html = {
                main: () => this.product.getDescriptionWithLineBreaks(),
                short: () => this.product.getDescriptionShortWithLineBreaks(),
                long: () => this.product.getDescriptionLongWithLineBreaks(),
            }

            return {...tab, html: tab.has ? html[tab.id]() : ''}
        },
        collapsedPreview() {
            // Prévia da aba atual; se ela estiver vazia, da primeira que tiver texto
            if (this.currentDescription.has) {
                return descriptionPreview(this.currentDescription.html)
            }

            const html = {
                main: () => this.product.getDescriptionWithLineBreaks(),
                short: () => this.product.getDescriptionShortWithLineBreaks(),
                long: () => this.product.getDescriptionLongWithLineBreaks(),
            }
            const filled = this.descriptionTabs.find((t) => t.has)

            return filled ? descriptionPreview(html[filled.id]()) : ''
        },
        chosenProductStoreHasChannels() {
            if (!this.chosenProductStore.channels) {
                return false
            }

            return !!this.chosenProductStore.channels.length
        },
        urlToReservedStockOrder() {
            if (!this.subscriber.isBackendBimer()) {
                return `/pedidos?custom=true&productIdList=${this.product.id}&statusList=${OrderStatusEnum.Pending}`
            }

            return `/pedidos?custom=true&productIdList=${this.product.firstChildId}&statusList=${OrderStatusEnum.Pending}`
        },
        urlToOrder() {
            if (!this.subscriber.isBackendBimer()) {
                return `/pedidos?custom=true&productIdList=${this.product.id}`
            }

            return `/pedidos?custom=true&productIdList=${this.product.firstChildId}`
        },
    },
    watch: {
        product: {
            deep: true,
            handler(p) {
                this.productStoreList = StoreModel.getStoreListWithIdAndName(p.getStoreList())
                // Mantém a loja escolhida quando o produto muda por uma associação feita aqui
                const current = this.productStoreList.find((store) => !!this.chosenStore && String(store.id) === String(this.chosenStore.id))
                this.chooseStoreFromList(current || this.productStoreList[0])
            }
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/product/styles/ProductDetail.scss';
</style>
